<script setup lang="ts">
import type { BaseFieldProps, FormErrors, GenericObject } from 'vee-validate'
import { genderOptions, getGenderLabel } from '~/utils/gender'
import { forbiddenRoleNames } from '~/utils/roleNames'
import type { ReactiveMember } from '~~/types/ReactiveMember'

const member = defineModel<ReactiveMember & {	legalRepresentative?: Ref<string | undefined, string | undefined>, legalRepresentativeAttrs?: Ref<BaseFieldProps & GenericObject> }>()

defineProps<{
	errors: FormErrors<Partial<Record<string, string | undefined>>>
	emailReadonly?: boolean
	showCsvTemplate?: boolean
	readonly?: boolean
}>()

defineEmits(['submit', 'cancel'])
</script>

<template>
	<form
		v-if="member"
		@submit.prevent="$emit('submit')"
	>
		<ion-item-divider>
			<ion-label>Persönliche Angaben</ion-label>
		</ion-item-divider>
		<VCIFormSpacer />
		<VCIFormItemSelect
			v-model="member.gender.value"
			v-bind="member.genderAttrs.value"
			label="Geschlecht"
			:error-text="errors.gender"
			:interface-options="{ header: 'Geschlecht auswählen:' }"
			:readonly="readonly"
		>
			<ion-select-option
				v-for="option in genderOptions"
				:key="option"
				:value="option"
			>
				{{ getGenderLabel(option) }}
			</ion-select-option>
		</VCIFormItemSelect>

		<VCIFormItemInput
			v-model="member.firstname.value"
			v-bind="member.firstnameAttrs.value"
			label="Vorname"
			is-required
			:error-text="errors.firstname"
			:readonly="readonly"
		/>
		<VCIFormItemInput
			v-model="member.lastname.value"
			v-bind="member.lastnameAttrs.value"
			label="Nachname"
			is-required
			:error-text="errors.lastname"
			:readonly="readonly"
		/>
		<VCIFormItemDateInput
			v-model="member.birthday.value"
			v-bind="member.birthdayAttrs.value"
			label="Geburtstag"
			:error-text="errors.birthday"
			:readonly="readonly"
		/>
		<VCIFormItemInput
			v-if="member.legalRepresentative"
			v-model="member.legalRepresentative.value"
			v-bind="member.legalRepresentativeAttrs?.value"
			label="Gesetzl. Vertreter/in"
			:error-text="errors.legalRepresentative"
			:readonly="readonly"
		/>
		<VCIFormSpacer />
		<ion-item-divider>
			<ion-label>Kontaktdaten</ion-label>
		</ion-item-divider>
		<VCIFormSpacer />
		<VCIFormItemInput
			v-model="member.email.value"
			v-bind="member.emailAttrs.value"
			label="E-Mail"
			type="email"
			:error-text="errors['contactInformation.email']"
			:readonly="emailReadonly || readonly"
		/>
		<VCIFormItemInput
			v-model="member.street.value"
			v-bind="member.streetAttrs.value"
			label="Straße"
			:error-text="errors['contactInformation.address.street']"
			:readonly="readonly"
		/>
		<VCIFormItemInput
			v-model="member.houseNumber.value"
			v-bind="member.houseNumberAttrs.value"
			label="Hausnummer"
			:error-text="errors['contactInformation.address.houseNumber']"
			:readonly="readonly"
		/>
		<VCIFormItemInput
			v-model="member.postalCode.value"
			v-bind="member.postalCodeAttrs.value"
			label="PLZ"
			:error-text="errors['contactInformation.address.postalCode']"
			:readonly="readonly"
		/>
		<VCIFormItemInput
			v-model="member.city.value"
			v-bind="member.cityAttrs.value"
			label="Ort"
			:error-text="errors['contactInformation.address.city']"
			:readonly="readonly"
		/>
		<VCIFormItemInput
			v-model="member.phone.value"
			v-bind="member.phoneAttrs.value"
			label="Telefonnummer"
			type="tel"
			:error-text="errors['contactInformation.phone']"
			:readonly="readonly"
		/>
		<VCIFormSpacer />
		<ion-item-divider>
			<ion-label>Kontodaten</ion-label>
		</ion-item-divider>
		<VCIFormSpacer />
		<VCIFormItemInput
			v-model="member.iban.value"
			v-bind="member.ibanAttrs.value"
			label="IBAN"
			:error-text="errors.iban"
			:readonly="readonly"
		/>
		<VCIFormSpacer />
		<ion-item-divider>
			<ion-label>Vereinsbezogene Angaben</ion-label>
		</ion-item-divider>
		<VCIFormSpacer />
		<VCIFormItemDateInput
			v-model="member.joinDate.value"
			v-bind="member.joinDateAttrs.value"
			label="Mitglied seit"
			:error-text="errors.joinDate"
			:readonly="readonly"
		/>
		<VCITagInput
			v-model="member.tags.value"
			v-bind="member.tagsAttrs.value"
			label="Rolle im Verein"
			:error-text="errors.tags"
			:forbidden-tags="forbiddenRoleNames"
			:readonly="readonly"
		/>
	</form>

	<ion-item-group
		v-if="showCsvTemplate"
		lines="full"
	>
		<ion-item-divider>
			<ion-label>Mitgliederliste (Vorlage)</ion-label>
		</ion-item-divider>
		<MemberListTemplateCard />
	</ion-item-group>
</template>

<style scoped lang="scss">
ion-item-divider {
	--background: var(--ion-color-grey-5);
	--inner-padding-top: 24px;
	--inner-padding-start: 24px;
	--inner-padding-bottom: 8px;
	--padding-start: 0;
	font-size: 14px;
	min-height: 14px;
}
</style>
